/**
 * Format a file size in bytes to a human-readable string (bytes, KB, or MB)
 * @param bytes - The file size in bytes
 * @returns A formatted string representation of the file size
 * @example
 * // Returns "512 bytes"
 * formatFileSize(512);
 *
 * @example
 * // Returns "1.50 KB"
 * formatFileSize(1536);
 *
 * @example
 * // Returns "2.50 MB"
 * formatFileSize(2621440);
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Validates if a file's extension is among the accepted extensions
 * @param file - The file to validate
 * @param acceptedExtensions - Array of accepted file extensions (with dots, e.g. ['.pdf', '.jpg'])
 * @returns Boolean indicating if the file extension is valid
 */
export const isValidFileExtension = (
  file: File,
  acceptedExtensions: readonly string[]
): boolean => {
  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return acceptedExtensions.includes(extension);
};

/**
 * Validates if a file's size is within the specified maximum
 * @param file - The file to validate
 * @param maxSizeBytes - Maximum allowed size in bytes
 * @returns Boolean indicating if the file size is valid
 */
export const isValidFileSize = (file: File, maxSizeBytes: number): boolean => {
  return file.size <= maxSizeBytes;
};
